/**
 * PlayingCard — CSS/SVG based playing card with a classic look.
 * Card notation: 2-char string like "AS" (Ace of Spades), "TH" (10 of Hearts),
 * "KD" (King of Diamonds). Face-down mode shows an ornate back pattern.
 *
 * Suits are hand-drawn inline SVGs so they render identically on every
 * platform (iOS otherwise coerces ♠ ♣ ♥ ♦ into colored emoji glyphs which
 * look mushy and off-brand).
 */
const SUIT_COLOR = { S: '#0a0a0a', C: '#0a0a0a', H: '#c11b1b', D: '#c11b1b' };

// Each returns a component that fills its container.
const SuitSpade = (p) => (
    <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" {...p}>
        <path
            d="M16 2C16 2 3.5 12 3.5 20.2c0 4.3 3 6.8 6.4 6.8 2.2 0 3.6-1 4.4-2.3-.4 2-1.7 3.6-3.4 4.5L10 32h12l-.9-2.8c-1.7-.9-3-2.5-3.4-4.5.8 1.3 2.2 2.3 4.4 2.3 3.4 0 6.4-2.5 6.4-6.8C28.5 12 16 2 16 2z"
            fill="currentColor"
        />
    </svg>
);
const SuitHeart = (p) => (
    <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" {...p}>
        <path
            d="M16 29.4S3 20.6 3 11.7C3 6.9 6.8 4 10.4 4c2.5 0 4.5 1.4 5.6 3.6C17.1 5.4 19.1 4 21.6 4 25.2 4 29 6.9 29 11.7c0 8.9-13 17.7-13 17.7z"
            fill="currentColor"
        />
    </svg>
);
const SuitDiamond = (p) => (
    <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" {...p}>
        <path
            d="M16 2c-1.2 3.6-4.6 9.5-11 14 6.4 4.5 9.8 10.4 11 14 1.2-3.6 4.6-9.5 11-14-6.4-4.5-9.8-10.4-11-14z"
            fill="currentColor"
        />
    </svg>
);
const SuitClub = (p) => (
    <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" {...p}>
        {/* three lobes */}
        <circle cx="16" cy="9" r="5.5" fill="currentColor" />
        <circle cx="9" cy="17" r="5.5" fill="currentColor" />
        <circle cx="23" cy="17" r="5.5" fill="currentColor" />
        {/* stem */}
        <path d="M13 20h6l2 10h-10z" fill="currentColor" />
    </svg>
);

const SUIT_GLYPHS = { S: SuitSpade, C: SuitClub, H: SuitHeart, D: SuitDiamond };
const RANK_DISPLAY = { T: '10', A: 'A', J: 'J', Q: 'Q', K: 'K' };

// Reusable pip renderer for corners / center
function Pip({ suit, size = 'sm' }) {
    const Glyph = SUIT_GLYPHS[suit] || SuitSpade;
    const dims = size === 'xl'
        ? 'w-6 h-6 md:w-8 md:h-8'
        : size === 'md'
            ? 'w-3.5 h-3.5 md:w-4 md:h-4'
            : 'w-2.5 h-2.5 md:w-3 md:h-3';
    return <Glyph className={dims} style={{ display: 'block' }} />;
}

export default function PlayingCard({ card, faceDown = false, small = false }) {
    const w = small ? 'w-14 h-20' : 'w-12 h-[72px] md:w-[70px] md:h-[100px]';

    if (faceDown || !card) {
        return (
            <div
                className={`${w} rounded-lg border border-[#FFD700]/40 shadow-lg overflow-hidden relative`}
                style={{
                    background: 'linear-gradient(135deg, #1a0f2e 0%, #0d0620 50%, #1a0f2e 100%)',
                }}
                data-testid="pc-facedown"
            >
                {/* Diamond pattern back */}
                <div className="absolute inset-1 rounded-md border border-[#FFD700]/30 flex items-center justify-center">
                    <div className="grid grid-cols-3 gap-0.5 opacity-40">
                        {Array.from({ length: 9 }).map((_, i) => (
                            <div key={i} className="w-2 h-2 rotate-45 border border-[#FFD700]/40" />
                        ))}
                    </div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="text-[#FFD700] font-black text-xs opacity-70 tracking-widest">BH</span>
                </div>
            </div>
        );
    }

    const rank = card[0];
    const suit = card[1];
    const color = SUIT_COLOR[suit] || SUIT_COLOR.S;
    const display = RANK_DISPLAY[rank] || rank;

    return (
        <div
            className={`${w} rounded-lg bg-white shadow-lg relative overflow-hidden border border-black/10`}
            style={{ backgroundImage: 'linear-gradient(180deg, #fff 0%, #f6f6f6 100%)' }}
            data-testid={`pc-${card}`}
        >
            {/* Top-left corner index */}
            <div className="absolute top-1 left-1.5 flex flex-col items-center leading-none" style={{ color }}>
                <span className="text-sm md:text-base font-black">{display}</span>
                <div className="mt-0.5"><Pip suit={suit} size="sm" /></div>
            </div>
            {/* Center pip */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ color }}>
                <div className="w-6 h-6 md:w-9 md:h-9"><Pip suit={suit} size="xl" /></div>
            </div>
            {/* Bottom-right corner index (rotated) */}
            <div className="absolute bottom-1 right-1.5 flex flex-col items-center leading-none rotate-180" style={{ color }}>
                <span className="text-sm md:text-base font-black">{display}</span>
                <div className="mt-0.5"><Pip suit={suit} size="sm" /></div>
            </div>
        </div>
    );
}
